import {SidebarCollapseIcon, SidebarExpandIcon} from '@primer/octicons-react'
import {ActionList, PageLayout} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {createContext, use, useEffect, useId, useMemo, useRef, useState} from 'react'

import {SidebarTooltip} from '../SidebarTooltip/SidebarTooltip'
import styles from './CollapsibleSidebar.module.css'

type CollapsibleSidebarContextValue = {
  isExpanded: boolean
  /**
   * Delayed mirror of `isExpanded` that stays in the old state during the
   * CSS width transition so descendants can keep their DOM stable while the
   * sidebar animates. On expand it flips immediately; on collapse it flips
   * after the transition duration (200 ms).
   */
  visualIsExpanded: boolean
  /** Stable DOM id of the sidebar wrapper, used by Toggle for aria-controls. */
  sidebarId?: string
  /** Whether the sidebar content is currently rendered inside a narrow-viewport Dialog. */
  isInDialog: boolean
  /**
   * Toggle the sidebar between expanded and condensed. When provided by
   * the surrounding `<CollapsibleSidebar>`, descendants like
   * `<CollapsibleSidebar.Toggle>` can read this from context instead of
   * having it threaded through props.
   */
  onToggle?: () => void
}

/**
 * Stable identity for the "no provider" default. We compare against this
 * sentinel in `useIsInsideCollapsibleSidebar` to distinguish between "I'm
 * being read because no provider is mounted" (in which case the default
 * is returned) and "I'm being read inside a real provider" (in which case
 * the provider passed a fresh object).
 */
const DEFAULT_CONTEXT_VALUE: CollapsibleSidebarContextValue = {
  isExpanded: true,
  visualIsExpanded: true,
  isInDialog: false,
}

export const CollapsibleSidebarContext = createContext<CollapsibleSidebarContextValue>(DEFAULT_CONTEXT_VALUE)

/**
 * Read the current expanded/collapsed state of the enclosing
 * `<CollapsibleSidebar>`. Defaults to `isExpanded: true` if no provider is
 * present, so individual primitives render correctly when used outside of a
 * collapsible layout (e.g. in a non-collapsible `PageLayout.Pane`).
 */
export function useCollapsibleSidebarContext(): CollapsibleSidebarContextValue {
  return use(CollapsibleSidebarContext)
}

/**
 * Returns true when the calling component is rendered inside a real
 * `<CollapsibleSidebar>` provider (or any consumer-supplied wrapper that
 * renders `CollapsibleSidebarContext`). Returns false when no provider is
 * mounted and the default fallback context is being used. Useful for
 * primitives that want to enforce contracts (e.g. requiring a
 * `LeadingVisual` on every item) only when actually used in a collapsible
 * layout.
 */
export function useIsInsideCollapsibleSidebar(): boolean {
  return use(CollapsibleSidebarContext) !== DEFAULT_CONTEXT_VALUE
}

export interface CollapsibleSidebarProps {
  children: ReactNode
  isExpanded: boolean
  /**
   * Optional aria-label. When provided, the wrapper exposes a `complementary`
   * landmark to assistive technologies. Omit when the children already provide
   * their own landmark to avoid nesting landmarks.
   */
  ariaLabel?: string
  className?: string
  /**
   * The width of the sidebar when expanded. Mirrors PageLayout.Pane width values.
   */
  expandedWidth?: 'small' | 'medium' | 'large'
  /**
   * Content pinned to the bottom of the sidebar. Stays visible via
   * `position: sticky; bottom: 0` as the page scrolls. Typically
   * contains a `<CollapsibleSidebar.Toggle />` and/or a feedback link.
   */
  footer?: ReactNode
  /**
   * Optional callback to toggle expanded ↔ condensed. When provided, it's
   * published via `CollapsibleSidebarContext` so descendants (e.g.
   * `<CollapsibleSidebar.Toggle>`) can read it from context instead of
   * threading it through props.
   */
  onToggle?: () => void
  'data-testid'?: string
}

const expandedWidthClass = {
  small: styles.expandedSmall,
  medium: styles.expandedMedium,
  large: styles.expandedLarge,
} as const

/**
 * A sidebar with a fixed icon-only condensed mode and an expanded mode.
 *
 * Unlike `PageLayout.Pane` with `resizable`, this component switches between
 * two discrete widths and does not provide a drag handle. Pair it with
 * `useCollapsibleSidebarState` for persisted expanded/collapsed state, and
 * render a `<CollapsibleSidebar.Toggle />` inside `children` to let users
 * change modes.
 *
 * Children rendered inside this component can call
 * `useCollapsibleSidebarContext()` (or use the `Item` primitive below) to
 * adapt their rendering to the current expanded/collapsed state without the
 * consumer having to thread the state through props.
 *
 * The wrapper is presentational by default — pass `ariaLabel` only when the
 * children do not already provide their own landmark.
 */
function CollapsibleSidebarRoot({
  children,
  isExpanded,
  ariaLabel,
  className,
  expandedWidth = 'small',
  footer,
  onToggle,
  'data-testid': dataTestId,
}: CollapsibleSidebarProps) {
  const Wrapper = ariaLabel ? 'aside' : 'div'
  const sidebarId = useId()
  const parentContext = useCollapsibleSidebarContext()

  // Delayed mirror of `isExpanded`: on expand it flips immediately so the
  // new label text reveals as the width grows; on collapse it stays true
  // for the duration of the CSS transition so the DOM stays stable while
  // the sidebar shrinks, then flips to false once it has settled.
  const [visualIsExpanded, setVisualIsExpanded] = useState(isExpanded)

  // Expand: flip synchronously during render (set-state-during-render) so
  // SidebarTooltip is suppressed on the very same frame — no intermediate
  // paint where the tooltip is briefly enabled.
  if (isExpanded && !visualIsExpanded) {
    setVisualIsExpanded(true)
  }

  useEffect(() => {
    if (!isExpanded) {
      // 200ms matches the CSS transition duration on .sidebar
      const id = setTimeout(() => setVisualIsExpanded(false), 200)
      return () => clearTimeout(id)
    }
  }, [isExpanded])

  const contextValue = useMemo(
    () => ({isExpanded, visualIsExpanded, sidebarId, isInDialog: parentContext.isInDialog, onToggle}),
    [isExpanded, visualIsExpanded, sidebarId, parentContext.isInDialog, onToggle],
  )
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isClipping, setIsClipping] = useState(false)

  const hasFooter = footer != null
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasFooter) return

    const observer = new IntersectionObserver(entries => setIsClipping(!entries[0]?.isIntersecting), {threshold: 0})
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasFooter])

  return (
    <CollapsibleSidebarContext value={contextValue}>
      <Wrapper
        id={sidebarId}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        data-expanded={isExpanded}
        className={clsx(styles.sidebar, isExpanded ? expandedWidthClass[expandedWidth] : styles.condensed, className)}
      >
        {children}
        {footer != null ? (
          <>
            <div ref={sentinelRef} className={styles.sentinel} />
            <div className={clsx(styles.footer, isClipping && styles.footerClipping)}>{footer}</div>
          </>
        ) : null}
      </Wrapper>
    </CollapsibleSidebarContext>
  )
}

export interface CollapsibleSidebarToggleProps {
  /**
   * Optional override for the surrounding `<CollapsibleSidebar>`'s
   * `isExpanded` value. When omitted, the Toggle reads from
   * `CollapsibleSidebarContext` so callers don't need to thread it
   * through props.
   */
  isExpanded?: boolean
  /**
   * Optional override for the surrounding `<CollapsibleSidebar>`'s
   * `onToggle` handler. When omitted, the Toggle reads from
   * `CollapsibleSidebarContext`. If neither is set, the Toggle is a no-op.
   */
  onToggle?: () => void
  /**
   * Override the default labels (Collapse sidebar / Expand sidebar).
   */
  collapseLabel?: string
  expandLabel?: string
  className?: string
}

/**
 * A toggle button intended to live at the bottom of a `<CollapsibleSidebar>`.
 * Uses `ActionList.Item` so the sizing, color, hover/focus states match the
 * NavList items in the sidebar. In condensed mode, the text label is
 * visually hidden by the `.sidebarCondensed` CSS overrides on the consumer.
 *
 * `isExpanded` and `onToggle` are read from `CollapsibleSidebarContext`
 * by default; pass them explicitly only when you need to override the
 * surrounding sidebar's state (e.g. for a story or test).
 */
function CollapsibleSidebarToggle({
  isExpanded: isExpandedProp,
  onToggle: onToggleProp,
  collapseLabel = 'Collapse sidebar',
  expandLabel = 'Expand sidebar',
  className,
}: CollapsibleSidebarToggleProps) {
  const ctx = use(CollapsibleSidebarContext)
  const isExpanded = isExpandedProp ?? ctx.isExpanded
  const onToggle = onToggleProp ?? ctx.onToggle
  const sidebarId = ctx.sidebarId
  const visualIsExpanded = isExpandedProp != null ? isExpanded : ctx.visualIsExpanded

  // The Toggle exists to collapse/expand the sidebar rail. When the sidebar
  // contents are rendered inside a Dialog (mobile breakpoint), there's no
  // rail to collapse — the dialog is open or closed — so suppress the button
  // entirely to avoid a misleading "Collapse sidebar" affordance.
  if (ctx.isInDialog) return null

  const label = isExpanded ? collapseLabel : expandLabel
  const visualLabel = visualIsExpanded ? collapseLabel : expandLabel
  const Icon = visualIsExpanded ? SidebarExpandIcon : SidebarCollapseIcon

  const content = (
    <ActionList className={clsx(styles.toggleList, className)}>
      <ActionList.Item onSelect={onToggle} aria-expanded={isExpanded} aria-controls={sidebarId}>
        <ActionList.LeadingVisual>
          <Icon />
        </ActionList.LeadingVisual>
        {visualLabel}
      </ActionList.Item>
    </ActionList>
  )

  if (isExpanded) return content

  return <SidebarTooltip text={label}>{content}</SidebarTooltip>
}

// Item primitive removed — consumers render their own items and read
// `useCollapsibleSidebarContext()` to branch on expanded/condensed state.

export const CollapsibleSidebar = Object.assign(CollapsibleSidebarRoot, {
  __SLOT__: PageLayout.Sidebar.__SLOT__, // Reuse the same slot as PageLayout.Sidebar for easy composition
  Toggle: CollapsibleSidebarToggle,
})

// Re-export sub-components as named exports so react-docgen-typescript can
// extract their prop types when referenced via `<ArgTypes of={...} />` in
// MDX docs. The compound-component pattern (Object.assign) hides these from
// the docgen plugin, which only walks top-level module exports.
export {CollapsibleSidebarRoot}
export {CollapsibleSidebarToggle}
